import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { useAuth } from './AuthContext';

interface OrbColors {
  blob1: string;
  blob2: string;
  blob3: string;
  blob4: string;
  blob5: string;
  backgroundColor: string;
  splitScreenEnabled: boolean;
  splitScreenImageUrl: string; // Legacy single image (backward compatibility)
  splitScreenImages: string[]; // Array of image URLs for rotation
  splitScreenOverlayColor: string;
  splitScreenOverlayOpacity: number;
  splitScreenImagePosition: string;
}

interface OrbContextType {
  orbColors: OrbColors;
  setOrbColors: React.Dispatch<React.SetStateAction<OrbColors>>;
  isLoading: boolean;
}

const defaultColors: OrbColors = {
  blob1: '#22d3ee', // cyan-400
  blob2: '#3b82f6', // blue-500
  blob3: '#14b8a6', // teal-500
  blob4: '#06b6d4', // cyan-500
  blob5: '#0891b2', // cyan-600
  backgroundColor: '#0f172a', // slate-900 (dark background)
  splitScreenEnabled: false,
  splitScreenImageUrl: '', // Legacy single image
  splitScreenImages: [], // Array for multiple images
  splitScreenOverlayColor: '#000000', // black overlay
  splitScreenOverlayOpacity: 0.3, // 30% opacity
  splitScreenImagePosition: 'center' // center positioning
};

const OrbContext = createContext<OrbContextType | undefined>(undefined);

export const OrbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [orbColors, setOrbColors] = useState<OrbColors>(defaultColors);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedFromFirestore, setHasLoadedFromFirestore] = useState(false);
  const { currentUser } = useAuth();
  
  const timestamp = new Date().toISOString();
  console.log(`🎨 [${timestamp}] 🚀 OrbProvider INITIALIZED/RENDERED`);
  console.log(`🎨 [${timestamp}] OrbProvider: Current colors:`, orbColors, 'HasLoaded:', hasLoadedFromFirestore);

  useEffect(() => {
    let listenerCount = 0;
    const timestamp = new Date().toISOString();
    console.log(`🎨 [${timestamp}] OrbContext: Starting Firestore listener...`);
    console.log(`🎨 [${timestamp}] OrbContext: Database instance:`, db);
    console.log(`🎨 [${timestamp}] OrbContext: Document path: settings/login_blob_colors`);
    
    // Debug Firebase config in production
    console.log(`🔧 [${timestamp}] Firebase Config Check:`, {
      hasApiKey: !!import.meta.env.VITE_FIREBASE_API_KEY,
      hasAuthDomain: !!import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      hasProjectId: !!import.meta.env.VITE_FIREBASE_PROJECT_ID,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN
    });
    
    // Debug authentication status
    console.log(`🔐 [${timestamp}] Auth Status:`, {
      isAuthenticated: !!currentUser,
      userEmail: currentUser?.email,
      userUID: currentUser?.uid
    });
    
    // Reset state when starting new listener
    setIsLoading(true);
    setHasLoadedFromFirestore(false);
    
    const docRef = doc(db, 'settings', 'login_blob_colors');
    console.log(`🎨 [${timestamp}] OrbContext: Document reference:`, docRef);
    
    // First, try to get the document once to see if it exists
    getDoc(docRef).then((docSnap) => {
      const timestamp = new Date().toISOString();
      console.log(`🎨 [${timestamp}] OrbContext: Initial document fetch:`, {
        exists: docSnap.exists(),
        data: docSnap.exists() ? docSnap.data() : null
      });
    }).catch((error) => {
      const timestamp = new Date().toISOString();
      console.error(`🎨 [${timestamp}] OrbContext: Error fetching document:`, error);
    });
    
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      listenerCount++;
      const timestamp = new Date().toISOString();
      console.log(`🎨 [${timestamp}] 📡 OrbContext: Firestore update #${listenerCount} received`, {
        exists: docSnap.exists(),
        id: docSnap.id,
        path: docSnap.ref.path,
        metadata: docSnap.metadata,
        fromCache: docSnap.metadata.fromCache,
        hasPendingWrites: docSnap.metadata.hasPendingWrites
      });
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log(`🎨 [${timestamp}] OrbContext: Raw Firestore data:`, data);
        
                // Use data from Firestore, with more robust fallback logic
                const newColors: OrbColors = {
                  blob1: data.blob1 && typeof data.blob1 === 'string' ? data.blob1 : defaultColors.blob1,
                  blob2: data.blob2 && typeof data.blob2 === 'string' ? data.blob2 : defaultColors.blob2,
                  blob3: data.blob3 && typeof data.blob3 === 'string' ? data.blob3 : defaultColors.blob3,
                  blob4: data.blob4 && typeof data.blob4 === 'string' ? data.blob4 : defaultColors.blob4,
                  blob5: data.blob5 && typeof data.blob5 === 'string' ? data.blob5 : defaultColors.blob5,
                  backgroundColor: data.backgroundColor && typeof data.backgroundColor === 'string' ? data.backgroundColor : defaultColors.backgroundColor,
                  splitScreenEnabled: typeof data.splitScreenEnabled === 'boolean' ? data.splitScreenEnabled : defaultColors.splitScreenEnabled,
                  splitScreenImageUrl: data.splitScreenImageUrl && typeof data.splitScreenImageUrl === 'string' ? data.splitScreenImageUrl : defaultColors.splitScreenImageUrl,
                  splitScreenImages: Array.isArray(data.splitScreenImages) ? data.splitScreenImages : defaultColors.splitScreenImages,
                  splitScreenOverlayColor: data.splitScreenOverlayColor && typeof data.splitScreenOverlayColor === 'string' ? data.splitScreenOverlayColor : defaultColors.splitScreenOverlayColor,
                  splitScreenOverlayOpacity: typeof data.splitScreenOverlayOpacity === 'number' ? data.splitScreenOverlayOpacity : defaultColors.splitScreenOverlayOpacity,
                  splitScreenImagePosition: data.splitScreenImagePosition && typeof data.splitScreenImagePosition === 'string' ? data.splitScreenImagePosition : defaultColors.splitScreenImagePosition
                };
        
        console.log(`🎨 [${timestamp}] OrbContext: New colors to apply:`, newColors);
        
        setOrbColors(prevColors => {
          const colorsChanged = JSON.stringify(prevColors) !== JSON.stringify(newColors);
          console.log(`🎨 [${timestamp}] OrbContext: Previous colors:`, prevColors);
          console.log(`🎨 [${timestamp}] OrbContext: New colors:`, newColors);
          console.log(`🎨 [${timestamp}] OrbContext: Colors changed?`, colorsChanged);
          
          // Force a state update even if colors seem the same
          if (colorsChanged) {
            console.log(`🎨 [${timestamp}] OrbContext: ✅ UPDATING COLORS`);
          } else {
            console.log(`🎨 [${timestamp}] OrbContext: ⚠️ NO CHANGE DETECTED`);
          }
          
          return newColors;
        });
        
        setHasLoadedFromFirestore(true);
      } else {
        console.log(`🎨 [${timestamp}] OrbContext: ❌ NO DOCUMENT FOUND!`);
        console.log(`🎨 [${timestamp}] OrbContext: hasLoadedFromFirestore:`, hasLoadedFromFirestore);
        console.log(`🎨 [${timestamp}] OrbContext: This will cause revert to defaults!`);
        
        if (!hasLoadedFromFirestore) {
          console.log(`🎨 [${timestamp}] OrbContext: 🔄 REVERTING TO DEFAULTS`);
          setOrbColors(defaultColors);
        } else {
          console.log(`🎨 [${timestamp}] OrbContext: ✋ Skipping default revert (already loaded)`);
        }
      }
      
      setIsLoading(false);
    }, (error) => {
      console.error('🎨 OrbContext: Firestore error:', error);
      console.error('🎨 OrbContext: Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      setOrbColors(defaultColors);
      setIsLoading(false);
    });

    return () => {
      const timestamp = new Date().toISOString();
      console.log(`🎨 [${timestamp}] OrbContext: Cleaning up listener`);
      unsubscribe();
    };
  }, [currentUser]);

  // Add a global debug function for testing
  useEffect(() => {
    (window as any).debugLoginSettings = {
      getCurrentColors: () => orbColors,
      testFirestoreRead: async () => {
        try {
          const docRef = doc(db, 'settings', 'login_blob_colors');
          const docSnap = await getDoc(docRef);
          console.log('🔍 Manual Firestore test:', {
            exists: docSnap.exists(),
            data: docSnap.exists() ? docSnap.data() : null
          });
          return docSnap.exists() ? docSnap.data() : null;
        } catch (error) {
          console.error('🔍 Manual Firestore test failed:', error);
          return null;
        }
      },
      resetToDefaults: () => {
        setOrbColors(defaultColors);
        console.log('🔄 Reset to default colors:', defaultColors);
      }
    };
  }, [orbColors]);

  return (
    <OrbContext.Provider value={{ orbColors, setOrbColors, isLoading }}>
      {children}
    </OrbContext.Provider>
  );
};

export const useOrbs = () => {
  const context = useContext(OrbContext);
  if (context === undefined) {
    throw new Error('useOrbs must be used within an OrbProvider');
  }
  return context;
};